/**
 * Helpers for GET /api/challenges/active/my-church.
 *
 * The route returns one of three shapes (see api/routes/challenges.js):
 *   { challenge: null, stats: null }              — no challenge running
 *   { challenge, stats: null }                    — not an approved church member
 *   { challenge, church, stats }                  — church-scoped getChallengeStats
 *
 * Stats are already windowed and church-scoped on the server. The UI must
 * not recompute them.
 */

/**
 * @typedef {'no-challenge'|'no-church'|'ready'} ChurchProgressStatus
 */

/**
 * @param {object|null|undefined} payload axios response.data
 */
export function interpretMyChurchProgress(payload) {
  if (!payload || payload.challenge == null) {
    return {
      status: 'no-challenge',
      challenge: null,
      church: payload?.church ?? null,
      stats: null
    };
  }

  if (payload.stats == null) {
    return {
      status: 'no-church',
      challenge: payload.challenge,
      church: payload.church ?? null,
      stats: null
    };
  }

  return {
    status: 'ready',
    challenge: payload.challenge,
    church: payload.church ?? null,
    stats: payload.stats
  };
}

/**
 * Season goals as returned on stats.goals. Empty when Decision 2 is still
 * open (no church goals seeded) and when the API sends none.
 */
export function getChallengeGoals(stats) {
  return Array.isArray(stats?.goals) && stats.goals.length > 0
    ? stats.goals
    : [];
}

/**
 * readersToday is not on getChallengeStats today. If the API later puts a
 * number on the payload, show it; otherwise hide the block. Do not invent
 * a count from other fields.
 */
export function getReadersToday(payload, stats) {
  const candidates = [
    stats?.readersToday,
    payload?.readersToday,
    payload?.church?.readersToday
  ];

  for (const value of candidates) {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
  }

  return null;
}

/**
 * Group still-needed chapters by book, preserving canonical order.
 */
export function groupChaptersByBook(chapters) {
  if (!Array.isArray(chapters) || chapters.length === 0) return [];

  const groups = [];
  let current = null;

  for (const chapter of chapters) {
    if (!current || current.book !== chapter.book) {
      current = {
        book: chapter.book,
        testament: chapter.testament,
        chapters: [chapter]
      };
      groups.push(current);
    } else {
      current.chapters.push(chapter);
    }
  }

  return groups;
}

/**
 * @returns {Set<number|string>|null}
 */
export function neededChapterIdSet(chaptersNeeded) {
  if (!Array.isArray(chaptersNeeded) || chaptersNeeded.length === 0) {
    return null;
  }
  return new Set(chaptersNeeded.map(chapter => chapter.id));
}

/**
 * Enumerating every still-needed chapter is useful when the gap is small
 * enough for a pastor to rally people. A full-Bible list (1,189 chips)
 * would bury the map on a phone.
 */
export const STILL_NEEDED_ENUMERATE_LIMIT = 120;

export function shouldEnumerateStillNeeded(chaptersNeeded, totalBibleChapters) {
  if (!Array.isArray(chaptersNeeded) || chaptersNeeded.length === 0) {
    return false;
  }
  if (totalBibleChapters && chaptersNeeded.length >= totalBibleChapters) {
    return false;
  }
  return chaptersNeeded.length <= STILL_NEEDED_ENUMERATE_LIMIT;
}
